import type { TrackerReferenceResolver } from '@nimbalyst/collab-bundle/editor';

type Resolution = ReturnType<TrackerReferenceResolver['resolve']>;

const NONE: readonly never[] = [];

// The state menu's optional methods, spelled out here so the wrapper forwards
// them whichever resolver type the bundle currently ships.
interface StatefulTrackerReferenceResolver extends TrackerReferenceResolver {
  statusOptions?(type: string): Array<{ value: string; label: string; color?: string; category?: string }>;
  updateItem?(itemId: string, updates: Record<string, unknown>): Promise<void>;
  archiveItem?(itemId: string, archive: boolean): Promise<void>;
  currentActor?(): { email: string; name?: string } | null;
  personName?(email: string): string | null;
  search?(query: string | null, options?: { limit?: number }): {
    options: Array<{ referenceKey: string; id: string; issueKey?: string; title: string; status?: string; type: string }>;
    typeFilter: string | null;
    searchQuery: string;
  };
}

export interface ForwardingTrackerReferences extends StatefulTrackerReferenceResolver {
  /** Point at the live resolver, or at nothing; subscribers re-read either way. */
  setTarget(target: StatefulTrackerReferenceResolver | null): void;
}

/**
 * A resolver that stands in for one that has not arrived yet.
 *
 * The editor reads its resolver once, at mount, and remounting a live
 * collaborative document to hand it a new one would destroy its Y.Doc behind a
 * flush. So the mount gets this stable object instead, and the host swaps the
 * target underneath it: until then every reference reads as loading, and each
 * swap notifies the mounted views so they re-read.
 *
 * It never owns its target. Disposing the target stays with whoever built it.
 */
export function createForwardingTrackerReferences(): ForwardingTrackerReferences {
  let target: StatefulTrackerReferenceResolver | null = null;
  let detach: (() => void) | null = null;
  const listeners = new Set<() => void>();
  // Loading answers are cached per key: `useSyncExternalStore` needs the same
  // object back until something changes.
  const loading = new Map<string, Resolution>();
  const noResults = (query: string | null) => ({ options: [], typeFilter: null, searchQuery: query ?? '' });
  const notify = () => {
    for (const listener of [...listeners]) listener();
  };
  // Optional capabilities, bound per target. Absent on the target means absent
  // here, which is how the views know to stay read-only; bound once per target
  // so a consumer sees a stable function between swaps.
  type Capabilities = Pick<StatefulTrackerReferenceResolver, 'statusOptions' | 'updateItem' | 'archiveItem' | 'currentActor' | 'personName'>;
  const bind = (next: StatefulTrackerReferenceResolver | null): Capabilities => ({
    statusOptions: next?.statusOptions ? (type) => next.statusOptions!(type) : undefined,
    updateItem: next?.updateItem ? (itemId, updates) => next.updateItem!(itemId, updates) : undefined,
    archiveItem: next?.archiveItem ? (itemId, archive) => next.archiveItem!(itemId, archive) : undefined,
    currentActor: next?.currentActor ? () => next.currentActor!() : undefined,
    personName: next?.personName ? (email) => next.personName!(email) : undefined,
  });
  let capabilities = bind(null);

  return {
    setTarget(next) {
      if (next === target) return;
      detach?.();
      target = next;
      capabilities = bind(next);
      loading.clear();
      detach = next ? next.subscribe(notify) : null;
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    resolve(key) {
      if (target) return target.resolve(key);
      let answer = loading.get(key);
      if (!answer) {
        answer = { state: 'loading', key };
        loading.set(key, answer);
      }
      return answer;
    },
    statementsAbout: (itemId) => target?.statementsAbout(itemId) ?? NONE,
    backlinks: (itemId) => target?.backlinks(itemId) ?? NONE,
    typeInfo: (type) => target?.typeInfo(type) ?? { type, displayName: type, color: 'var(--nim-text-muted)', icon: '' },
    statusInfo: (item) => target?.statusInfo(item) ?? null,
    fieldValue: (item, fieldName) => target?.fieldValue(item, fieldName),
    optionLabel: (type, fieldName, value) => target?.optionLabel(type, fieldName, value) ?? value,
    relationshipTargets: (item, fieldName) => target?.relationshipTargets(item, fieldName) ?? [],
    predicateLabel: (id) => target?.predicateLabel(id) ?? id,
    openItem: (itemId) => target?.openItem?.(itemId),
    // Always present: the editor decides at mount whether it can insert
    // references, before the real resolver may have arrived.
    search: (query, options) => target?.search?.(query, options) ?? noResults(query),
    // Forwarded explicitly (a method missing here makes every card read-only),
    // and only while the current target has it.
    get statusOptions() { return capabilities.statusOptions; },
    get updateItem() { return capabilities.updateItem; },
    get archiveItem() { return capabilities.archiveItem; },
    get currentActor() { return capabilities.currentActor; },
    get personName() { return capabilities.personName; },
    dispose() {
      detach?.();
      detach = null;
      target = null;
      capabilities = bind(null);
      listeners.clear();
    },
  };
}
